import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, Search, RefreshCw, AlertTriangle, CheckCircle, Loader } from 'lucide-react';

const VoucherMaintenancePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [missingVouchers, setMissingVouchers] = useState([]);
  const [selectedVouchers, setSelectedVouchers] = useState([]);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [user]);

  const checkMissingVouchers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/admin/vouchers/check-missing');
      setMissingVouchers(response.data.missing_vouchers || []);
      
      if (response.data.total_missing === 0) {
        setSuccess('✅ No se encontraron fichas faltantes. Todos los vouchers están sincronizados.');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al verificar fichas');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedVouchers.length === missingVouchers.length) {
      setSelectedVouchers([]);
    } else {
      setSelectedVouchers(missingVouchers.map(v => v.voucher_id));
    }
  };

  const handleSelectVoucher = (voucherId) => {
    if (selectedVouchers.includes(voucherId)) {
      setSelectedVouchers(selectedVouchers.filter(id => id !== voucherId));
    } else {
      setSelectedVouchers([...selectedVouchers, voucherId]);
    }
  };

  const handleRegenerate = async () => {
    if (selectedVouchers.length === 0) {
      setError('Selecciona al menos un voucher para regenerar');
      return;
    }

    if (!window.confirm(`¿Regenerar ${selectedVouchers.length} fichas en Mikrotik?`)) {
      return;
    }

    try {
      setRegenerating(true);
      setError('');
      setSuccess('');

      const response = await api.post('/api/admin/vouchers/regenerate', {
        voucher_ids: selectedVouchers
      });

      setSuccess(`✅ ${response.data.results.regenerated}/${response.data.results.total} fichas regeneradas exitosamente`);
      
      if (response.data.results.failed > 0) {
        setError(`⚠️ ${response.data.results.failed} fichas fallaron: ${response.data.results.errors.join(', ')}`);
      }

      // Recargar lista
      setSelectedVouchers([]);
      checkMissingVouchers();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al regenerar fichas');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-8 h-8 text-orange-500" />
                <div>
                  <h1 className="text-2xl font-bold">Mantenimiento de Fichas</h1>
                  <p className="text-sm text-gray-500">Verificar y regenerar fichas faltantes</p>
                </div>
              </div>
            </div>
            <button
              onClick={checkMissingVouchers}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              <Search className="w-5 h-5" />
              <span>{loading ? 'Verificando...' : 'Verificar Fichas'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <button onClick={() => setError('')} className="absolute top-0 right-0 px-4 py-3">
              <span className="text-2xl">&times;</span>
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{success}</span>
            <button onClick={() => setSuccess('')} className="absolute top-0 right-0 px-4 py-3">
              <span className="text-2xl">&times;</span>
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <Loader className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Verificando fichas en Mikrotik...</p>
          </div>
        ) : missingVouchers.length === 0 ? (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-12 text-center`}>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Sistema Sincronizado</h3>
            <p className="text-gray-500 mb-6">No se encontraron fichas faltantes en Mikrotik</p>
            <button
              onClick={checkMissingVouchers}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Verificar Nuevamente
            </button>
          </div>
        ) : (
          <>
            {/* Header de acciones */}
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-4 mb-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-red-600">
                    ⚠️ {missingVouchers.length} Fichas Faltantes en Mikrotik
                  </h2>
                  <p className="text-sm text-gray-500">
                    Estas fichas están en la BD pero NO en el router
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleSelectAll}
                    className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
                  >
                    {selectedVouchers.length === missingVouchers.length ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
                  </button>
                  <button
                    onClick={handleRegenerate}
                    disabled={selectedVouchers.length === 0 || regenerating}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`w-5 h-5 ${regenerating ? 'animate-spin' : ''}`} />
                    <span>
                      {regenerating ? 'Regenerando...' : `Regenerar (${selectedVouchers.length})`}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Lista de vouchers */}
            <div className="space-y-3">
              {missingVouchers.map((voucher) => (
                <div
                  key={voucher.voucher_id}
                  className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow`}
                >
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedVouchers.includes(voucher.voucher_id)}
                      onChange={() => handleSelectVoucher(voucher.voucher_id)}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <span className="text-gray-500 text-sm">Código:</span>
                        <p className="font-bold text-lg">{voucher.code}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-sm">Plan:</span>
                        <p className="font-semibold">{voucher.plan_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-sm">Router:</span>
                        <p className="font-semibold">{voucher.router_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-sm">Vendedor:</span>
                        <p className="font-semibold">{voucher.seller_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-500">Creado:</span>
                      <p className="text-sm">{new Date(voucher.created_at).toLocaleDateString('es-MX')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VoucherMaintenancePage;
